import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ChevronRight, LogOut } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import Navbar from "../components/Navbar";
import PackageBadge from "../components/PackageBadge";

const initials = (name) => (name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

export default function MyPlayers() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [players, setPlayers] = useState(null);
  // Everyone lands on the Player Dashboard.
  const playerLink = (id) => `/home/${id}`;

  useEffect(() => {
    let cancelled = false;
    api.myPlayers().then((data) => {
      if (cancelled) return;
      setPlayers(data || []);
      // If there's only one player, drop the user straight on their page.
      if ((data || []).length === 1) {
        navigate(playerLink(data[0].id), { replace: true });
      }
    }).catch(() => { if (!cancelled) setPlayers([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  if (players === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--org-accent)]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[var(--org-accent)] mb-2">Welcome</div>
            <h1 className="font-heading text-3xl lg:text-4xl uppercase text-white">
              {user?.name || user?.email?.split("@")[0]}
            </h1>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-2 px-4 py-2 border border-white/20 text-[#a0a0a0] text-[10px] uppercase tracking-[0.3em] font-bold hover:text-white"
            data-testid="my-players-logout"
          >
            <LogOut className="w-3 h-3" /> Sign Out
          </button>
        </div>

        <div className="mb-4 text-xs uppercase tracking-[0.4em] text-[#757575]">Your Players</div>

        {players.length === 0 ? (
          <div className="border border-white/10 bg-[#101010] p-10 text-center text-[#a0a0a0]" data-testid="my-players-empty">
            No player access has been granted to your account yet. Reach out to your coach.
          </div>
        ) : (
          <div className="space-y-3" data-testid="my-players-list">
            {players.map((p) => (
              <button
                key={p.id}
                onClick={() => navigate(playerLink(p.id))}
                className="w-full flex items-center gap-4 p-4 border border-white/10 bg-[#101010] hover:border-[var(--org-accent)] text-left group"
                style={{ transition: "border-color 0.2s ease" }}
                data-testid={`my-player-${p.id}`}
              >
                <div className="w-14 h-14 border border-white/10 bg-[#141414] flex items-center justify-center overflow-hidden shrink-0">
                  {p.headshot_url ? (
                    <img src={p.headshot_url} alt={p.name} className="w-full h-full object-cover object-top" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : (
                    <span className="text-sm text-[var(--org-accent)] font-bold">{initials(p.name)}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-heading text-xl uppercase tracking-tight text-white truncate">{p.name}</div>
                    {p.league === "HS" && p.bio?.package_name && (
                      <PackageBadge name={p.bio.package_name} status={p.bio.package_status} testId={`my-player-package-${p.id}`} />
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs">
                    <span className="text-[var(--org-accent)] uppercase tracking-widest font-bold">{p.league}</span>
                    <span className="text-[#a0a0a0]">{p.team}</span>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-[#a0a0a0] group-hover:text-[var(--org-accent)]" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
